import { AlignmentsLayer } from '@openlayers/alignments/AlignmentsLayer';
import { ModelsLayer } from '@openlayers/models/ModelsLayer';
import { ModelOutlineLayer } from '@openlayers/models/ModelOutlineLayer';
import { isIfcModel } from '@openlayers/models/model-layer-heuristic';
import { featuresExtent } from '@openlayers/ol-utils';
import { PipesLayer } from '@openlayers/pipes/PipesLayer';
import { GtPointsLayer } from '@openlayers/points/GtPointsLayer';
import { StringlinesLayer } from '@openlayers/stringlines/StringlinesLayer';
import { ModelsPanelToolbar } from '@pages/map/ui/models/ModelsPanelToolbar';
import { Events, EventsGlobal } from '@shared/Events';
import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { Tree as TreeNative, TreeNode } from '@shared/ui-native/Tree';
import { Tree as TreeComponent, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { TreeToolbarResponsive } from '@shared/ui-react/TreeToolbar';
import { Catch } from '@shared/ui-react/Catch';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { countsIncludesModels, decodeHtmlEntities, getFilenameWithVersion } from '@shared/utils/utils';
import { action, makeObservable, observable } from 'mobx';
import { Observer } from 'mobx-react';
import * as olExtent from 'ol/extent';
import * as React from 'react';
import { StructureLayer } from '@shared/openlayers/models/StructureLayer';
import { ModelService } from '@shared/models/model-service';
import { TekPointsLayer } from '@shared/openlayers/points/TekPointsLayer';
import { BackgroundMaps } from '@openlayers/BackgroundMaps';
import { RasterModel } from '../raster-model';
import { OutlineModel } from '../outline-model';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';
import { IContextMenuItem, renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';
import { FileInfoDialog } from '@shared/files/ui/FileInfoDialog';
import { CreateEditFolderDialog } from '@pages/files/dialogs/CreateEditFolderDialog';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { WorkflowReviewTaskEditDialog } from '@pages/tasks/workflow-review-tasks/WorkflowReviewTaskEditDialog';
import { WorkflowApproveDialog } from '@pages/tasks/WorkflowApproveDialog';
import { WorkflowsService } from '@shared/services/WorkflowsService';
import { Notify } from '@shared/ui-native/Notify';

/* ─── Interfaces ─── */

interface IMapLayers {
    models?: ModelsLayer;
    modelsOutline?: ModelOutlineLayer;
    pipes?: PipesLayer;
    stringlines?: StringlinesLayer;
    points?: GtPointsLayer;
    tekPoints?: TekPointsLayer;
    alignments?: AlignmentsLayer;
    structures?: StructureLayer;
}

export type Model = RasterModel | OutlineModel;

/* ─── MobX Store — single source of truth for tree UI state ─── */

export class ModelsPanelTreeStore {
    @observable.ref checkedIds: (number | string)[] = [];
    @observable.ref loadingIds: (number | string)[] = [];
    @observable.ref errorIds: Map<number | string, string> = new Map();
    @observable.ref selectedId: number | string | null = null;
    @observable.ref expandedIds: (number | string)[] = [];
    @observable filterText = '';
    @observable hideEmptyFolders = true;
    @observable expandOnStart = 'all';
    @observable nodesRevision = 0;
    activeModels: Model[] = [];

    constructor() {
        makeObservable(this);
    }

    @action setCheckedIds(ids: (number | string)[]) {
        this.checkedIds = ids;
    }

    @action setChecked(id: number | string, checked: boolean) {
        const set = new Set(this.checkedIds);
        if (checked) set.add(id);
        else set.delete(id);
        this.checkedIds = Array.from(set);
        if (!checked) {
            this.clearError(id);
        }
    }

    @action setLoading(id: number | string, loading: boolean) {
        const set = new Set(this.loadingIds);
        if (loading) set.add(id);
        else set.delete(id);
        this.loadingIds = Array.from(set);
    }

    @action setError(id: number | string, message: string) {
        const map = new Map(this.errorIds);
        map.set(id, message);
        this.errorIds = map;
    }

    @action clearError(id: number | string) {
        const map = new Map(this.errorIds);
        map.delete(id);
        this.errorIds = map;
    }

    @action setSelectedId(id: number | string | null) {
        this.selectedId = id;
    }

    @action setExpandedIds(ids: (number | string)[]) {
        this.expandedIds = ids;
    }

    @action setFilterText(text: string) {
        this.filterText = text;
    }

    @action setHideEmptyFolders(value: boolean) {
        this.hideEmptyFolders = value;
    }

    @action setExpandOnStart(value: string) {
        this.expandOnStart = value;
    }

    @action bumpNodesRevision() {
        this.nodesRevision++;
    }
}

export const modelsPanelStore = new ModelsPanelTreeStore();

/* ─── ModelsPanel ─── */

export class ModelsPanel implements IStateful<IMapPageStateModelsPanel> {
    isEnabled = true;
    private treeWrapperEl: HTMLElement = null;
    readonly store: ModelsPanelTreeStore;
    tree: TreeNative = null;

    modelsInLoading: Set<number>;
    private featuresLoadedMap = new Map<string, boolean>();
    queueFocusIdOrUuid: number | string;

    @observable settings = {
        layerOpacity: 100,
        layerOpacityModels: 100,
        layerOpacityLines: 100,
        layerOpacityDrawings: 100,
    };

    constructor(
        public panelWrapper: HTMLElement,
        private readonly treeDataPromise: Promise<ITreeData>,
        private readonly layers: IMapLayers,
        private readonly backgroundMaps: BackgroundMaps,
        store: ModelsPanelTreeStore = modelsPanelStore
    ) {
        this.store = store;
        makeObservable(this);

        ReactUtils.safeRender(
            <Catch>
                <ModelsPanelToolbar
                    pointsLayer={this.layers.points}
                    stringlinesLayer={this.layers.stringlines}
                    settings={this.settings}
                    onLayerOpacityChange={this.setMasterLayerOpacity}
                    onLayerOpacityModelsChange={this.setLayerOpacityModels}
                    onLayerOpacityLinesChange={this.setLayerOpacityLines}
                    onLayerOpacityDrawingsChange={this.setLayerOpacityDrawings}
                />
            </Catch>,
            document.querySelector('#js-app__models-panel__toolbar-wrapper')
        );

        this.modelsInLoading = new Set();

        if (treeDataPromise) {
            this.init();
        }
    }

    enabled() {
        return this.isEnabled;
    }

    private convertNativeNode = (node: TreeNode): TreeNodeData => {
        const isFolder = node.isFolder();
        // Prefix folder IDs to prevent collision with file IDs — the backend uses
        // the same numeric sequence for both, so e.g. folder id=3 and file id=3
        // can coexist in the same tree, which would cause the file to appear
        // checked whenever the folder id ends up in checkedIds or vice-versa.
        const treeId: number | string = isFolder ? `folder-${node.id}` : node.id;
        return {
            id: treeId,
            uuid: node.uuid,
            // Use ajaxData.name (the raw server filename) rather than node.text,
            // because Tree.renderNodeCountsManual() injects HTML like
            // "file.dwg <span>(7)</span>" into node.text. Stripping the tags
            // would leave "(7)" as part of the name and break icon/extension lookup.
            // For files with an older version, getFilenameWithVersion appends "(v2/3)".
            name: decodeHtmlEntities(isFolder ? node.ajaxData?.name ?? '' : getFilenameWithVersion(node.ajaxData ?? {}) ?? node.ajaxData?.text ?? ''),
            type: isFolder ? 'folder' : node.isLink() ? 'link' : 'file',
            secondaryIcon:
                node.secondaryIcon && this.store.checkedIds.includes(treeId) ? (
                    <i className={node.secondaryIcon} title={node.secondaryIconTooltip ?? undefined} aria-label={node.secondaryIconTooltip ?? undefined} />
                ) : undefined,
            error: this.store.errorIds.get(treeId),
            children: node.children?.filter(child => !child.state.hidden && !child.state.filtered).map(this.convertNativeNode),
            data: node,
        };
    };

    // Recursively drops folders whose entire subtree is empty (all-folder descendants
    // with no file/link leaves). Runs after convertNativeNode has already stripped
    // hidden/filtered native-tree children, so a folder with children: [] here really
    // has nothing displayable inside.
    private removeEmptyFolders = (nodes: TreeNodeData[]): TreeNodeData[] =>
        nodes.reduce<TreeNodeData[]>((acc, node) => {
            if (node.type !== 'folder') {
                acc.push(node);
            } else {
                const filteredChildren = this.removeEmptyFolders(node.children ?? []);
                if (filteredChildren.length > 0) acc.push({ ...node, children: filteredChildren });
            }
            return acc;
        }, []);

    private getViewNodes(): TreeNodeData[] {
        if (!this.tree) return [];
        void this.store.nodesRevision; // mobx tracking: native tree state.hidden is not observable

        const raw = this.tree.children.filter(node => !node.state.hidden && !node.state.filtered).map(this.convertNativeNode);
        return this.store.hideEmptyFolders ? this.removeEmptyFolders(raw) : raw;
    }

    private getAllFileNodes(): TreeNode[] {
        if (!this.tree) return [];

        return this.tree.getChildren(true).filter(node => node.isFile());
    }

    /* ─── Init ─── */

    private async init() {
        this.panelWrapper.classList.add('overlay-loading');

        try {
            const treeData: ITreeData = await this.treeDataPromise;
            this.panelWrapper.classList.remove('overlay-loading');

            this.treeWrapperEl = document.querySelector('#js-app__models-panel__tree-wrapper');
            if (!this.treeWrapperEl) return;

            this.tree = new TreeNative({
                renderView: false,
                showRoot: false,
                showCheckboxes: true,
                selectionCheckbox: false,
                checkOnHighlightSelected: true,
                hideNonPresentable: true,
                propagateCheckbox: 'first_level_files',
                hideEmptyFoldersAlways: true,
                showContextMenuInfo: true,
                storageKey: 'map-models-panel',
                childFilter: c => {
                    if (c.tolerance) return true;
                    if (kuuraModel.isMap(c)) return true;
                    if ((c as any).parseStatus) return true;
                    return kuuraModel.hasAnyDrawableLayer(c);
                },
            });

            this.tree.initAndRender(treeData, true);

            // Sync persisted settings from native tree to MobX store
            const storedState = this.tree.getStoredState();
            if (storedState?.expandOnStart) {
                this.store.setExpandOnStart(storedState.expandOnStart);
            }
            if (storedState?.hideEmptyFolders != null) {
                this.store.setHideEmptyFolders(storedState.hideEmptyFolders);
            }

            // Always mirror native tree's actual expanded state into the DS store.
            // The DS Tree only respects expandedIds, not expandOnStart.
            // Only populate when empty to avoid overwriting user's expanded state
            // if init() is called again (e.g. due to panel re-creation on resize).
            if (this.store.expandedIds.length === 0) {
                const expandedDsIds: (number | string)[] = [];
                this.tree.each(c => {
                    if (c.isFolder() && c.state.expanded) {
                        expandedDsIds.push(`folder-${c.id}`);
                    }
                }, true);
                this.store.setExpandedIds(expandedDsIds);
            }

            if (this.getViewNodes().length === 0) {
                this.treeWrapperEl.innerHTML = `<div class="panel-no-items"><i class="iki iki-models"></i><br>${kuura.getMessage('js_logpoints.no_models')}</div>`;
                return;
            }

            this.mountTree();

            if (this.stateful.queuedRestoreState) {
                this.stateful.restoreState(this.stateful.queuedRestoreState);
            }
            if (this.queueFocusIdOrUuid) {
                this.focusItem(this.queueFocusIdOrUuid);
                this.queueFocusIdOrUuid = null;
            }
        } catch (e) {
            this.panelWrapper.classList.remove('overlay-loading');
            console.error('Failed to load models tree', e);
        }
    }

    /* ─── Mount React tree (once) ─── */

    private mountTree() {
        if (!this.treeWrapperEl) return;

        ReactUtils.safeRender(
            <Catch>
                <Observer>
                    {() => {
                        return (
                            <>
                                {/* Fixed slot: toolbar + selection feedback never shrink or scroll */}
                                <div style={{ flexShrink: 0 }}>
                                    <TreeToolbarResponsive
                                        filterText={this.store.filterText}
                                        onFilterChange={text => this.store.setFilterText(text)}
                                        onExpandAll={() => {
                                            this.store.setExpandedIds(Array.from(collectAllFolderIds(this.getViewNodes())));
                                        }}
                                        onCollapseAll={() => this.store.setExpandedIds([])}
                                        hideEmptyFolders={this.store.hideEmptyFolders}
                                        hideEmptyFoldersAlways={false}
                                        onToggleHideEmptyFolders={() => {
                                            this.store.setHideEmptyFolders(!this.store.hideEmptyFolders);
                                            this.tree?.toggleHideEmptyFolders();
                                            setTimeout(() => this.store.bumpNodesRevision(), 50);
                                        }}
                                        expandOnStart={this.store.expandOnStart}
                                        onSetExpandOnStart={value => {
                                            this.store.setExpandOnStart(value);
                                            this.tree?.setExpandOnStart(value as any);
                                        }}
                                    />
                                    <TreeSelectionFeedback
                                        checkedCount={this.store.checkedIds.length}
                                        loadedItemsCount={this.store.checkedIds.length}
                                        totalItemsCount={this.getAllFileNodes().length}
                                        onClearSelection={this.clearSelection}
                                        onSelectAll={this.selectAll}
                                        countTooltip={kuura.getMessage('js_map.models.selected_count_tooltip')}
                                        additionalItems={this.clearMenuItem}
                                    />
                                </div>
                                {/* Flexible slot: tree fills the remaining height; its internal
                                overflow-auto container provides the single scrollbar.
                                min-h-0 overrides the Tree's default min-h-[200px] so it can
                                shrink instead of pushing content out of reach. */}
                                <TreeComponent
                                    className='min-h-0 flex-1'
                                    nodes={this.getViewNodes()}
                                    showRoot={false}
                                    checkable={true}
                                    checkedIds={this.store.checkedIds}
                                    selectedId={this.store.selectedId}
                                    expandedIds={this.store.expandedIds}
                                    onExpandedIdsChange={ids => {
                                        this.store.setExpandedIds(ids);
                                        if (this.tree) {
                                            this.tree.each(node => {
                                                if (node.isFolder()) {
                                                    node.state.expanded = ids.includes(`folder-${node.id}`);
                                                }
                                            }, true);
                                            this.tree.persistState();
                                        }
                                    }}
                                    expandOnStart='firstLevel'
                                    hideEmptyFolders={this.store.hideEmptyFolders}
                                    sortChildren={true}
                                    loadingIds={this.store.loadingIds}
                                    filterText={this.store.filterText}
                                    renderLabel={this.renderLabel}
                                    onNodeSelected={this.handleSelect}
                                    onNodeChecked={this.handleCheck}
                                    onNodeUnchecked={this.handleUncheck}
                                    onContextMenu={this.handleContextMenu}
                                />
                            </>
                        );
                    }}
                </Observer>
            </Catch>,
            this.treeWrapperEl
        );
    }

    /* ─── Tree callbacks ─── */

    private handleSelect = (node: TreeNodeData) => {
        this.store.setSelectedId(node.id);

        const treeNode = node.data as TreeNode;
        if (!treeNode?.ajaxData?.layerCounts) return;

        const extent = this.getNodeExtent(treeNode);
        if (this.store.checkedIds.includes(node.id) && !olExtent.isEmpty(extent)) {
            Events.trigger(EventsGlobal.MAP_FIT_EXTENT, { extent });
        }
    };

    private handleCheck = (node: TreeNodeData) => {
        if (node.type !== 'file') return;
        this.store.setChecked(node.id, true);
        const nativeNode = this.tree.getByUuid(node.uuid);
        this.loadFile(nativeNode);
    };

    private handleUncheck = (node: TreeNodeData) => {
        if (node.type !== 'file') return;
        this.store.setChecked(node.id, false);

        const nativeNode = this.tree.getByUuid(node.uuid);
        this.unloadFile(nativeNode);
    };

    private handleContextMenu = (node: TreeNodeData, position: { x: number; y: number }) => {
        const items: IContextMenuItem[] = [];
        const nativeNode = node.data as TreeNode;

        // Custom: workflow approve (custom permission, files only)
        if (node.type === 'file' && kuura.hasPrivilege('WORKFLOW_TASK_PAGE')) {
            items.push({
                label: __('workflows.action.start_or_approve'),
                icon: 'iki iki-approve',
                action: () => this.openWorkflowItem(node.uuid),
            });
        }

        if (node.type === 'file' || node.type === 'link') {
            const isLink = node.type === 'link';
            const modelId = isLink ? nativeNode.ajaxData?.modelId : nativeNode.ajaxData?.id;
            const modelUuid = isLink ? nativeNode.ajaxData?.modelUuid : nativeNode.ajaxData?.uuid;
            const folderId = isLink ? nativeNode.ajaxData?.model?.folderId : nativeNode.ajaxData?.folderId;

            // Download
            if (kuura.hasPrivilege('Download_Model')) {
                items.push({
                    label: __('general.download_file'),
                    icon: 'iki iki-download',
                    action: () => {
                        window.open(location.origin + kuura.getContextPath() + `/api/1/file/${modelId}/get`, '_blank')?.focus();
                    },
                });
            }

            // Open in Files page
            if (!window.location.href.includes('models.htm') && kuura.hasPrivilege('MODEL_PAGE')) {
                items.push({
                    label: __('js_menu.open_in_files_page'),
                    icon: 'iki iki-external',
                    action: () => {
                        window.open(location.origin + kuura.getContextPath() + '/models.htm#/' + folderId + '/' + modelId, '_blank')?.focus();
                    },
                });
            }

            // Show details
            items.push({
                label: __('general.show_details'),
                icon: 'iki iki-info',
                action: () => FileInfoDialog.open(modelUuid, true, true),
            });

            // Show model data (toggle 3D content for checked map layers with features)
            const layerCounts = nativeNode.ajaxData?.layerCounts;
            if (this.store.checkedIds.includes(node.id) && layerCounts?.mapLayers > 0 && layerCounts?.features > 0) {
                items.push({
                    label: __('js_map.show_model_data'),
                    action: () => {
                        if (this.featuresLoadedMap.has(modelUuid)) {
                            this.featuresLoadedMap.delete(modelUuid);
                            Events.trigger('unload_3d_content_' + node.uuid, { nodeToBeLoaded: nativeNode });
                        } else {
                            this.featuresLoadedMap.set(modelUuid, true);
                            Events.trigger('load_3d_content_' + node.uuid, { nodeToBeLoaded: nativeNode });
                        }
                    },
                    checked: this.featuresLoadedMap.has(modelUuid) || false,
                });
            }
        } else {
            // Folder: show details
            items.push({
                label: __('general.show_details'),
                icon: 'iki iki-info',
                action: () => CreateEditFolderDialog.info(nativeNode),
            });
        }

        if (!items.length) return;

        Events.trigger('map-hide-overlays');
        renderContextMenuAtPosition(items, position.x, position.y, 'tree-context-menu', () => {});
    };

    private renderLabel = (node: TreeNodeData) => {
        if (node.type !== 'folder') return node.name;

        const nativeNode = node.data as TreeNode;
        if (!nativeNode?.isFolder()) return node.name;

        const files = nativeNode.getChildren(false, false).filter(c => c.isFileOrLink());
        const totalCount = files.length;
        if (totalCount === 0) return node.name;

        const checkedCount = files.filter(c => this.store.checkedIds.includes(c.id)).length;
        const countText = checkedCount > 0 && checkedCount !== totalCount ? `(${checkedCount}/${totalCount})` : `(${totalCount})`;

        return (
            <>
                {node.name} <span style={{ color: 'var(--ik-color-text-weakest)' }}>{countText}</span>
            </>
        );
    };

    /* ─── File loading / unloading (map layer logic) ─── */

    private async loadFile(node: TreeNode) {
        let counts = node.ajaxData?.layerCounts;

        // If layerCounts missing, fetch from API
        if (!counts) {
            try {
                const info = await ModelService.getModelInfoByHeadUuid(node.headUuid);
                node.ajaxData.layerCounts = info.layerCounts;
                node.ajaxData.uuid = info.uuid;
                node.ajaxData.name = info.name;
                node.text = info.name;
                node.uuid = info.uuid;
                node.id = info.id;
                counts = info.layerCounts;
            } catch {
                console.warn('Failed to fetch model info', node.headUuid);
                this.store.setChecked(node.id, false);
                this.store.setError(node.id, 'Failed to load model');
                return;
            }
        }

        if (!counts) return;

        this.store.setLoading(node.id, true);
        this.store.clearError(node.id);

        try {
            if (counts.alignments > 0) {
                await this.layers.alignments.fetchAndDrawFile(node.id);
            }

            const isMapLayerWith3DDrawableContent: boolean = counts.mapLayers > 0 && counts.features > 0;
            if (!isMapLayerWith3DDrawableContent && (counts.stringlines || counts.breaklineLayers || counts.features || counts.pointsLayers)) {
                await this.drawOutlinesAndPoints(node, counts);
            }

            if (counts.triangleMeshes > 0) {
                if (this.modelsInLoading.has(node.id)) return;
                this.modelsInLoading.add(node.id);

                const isIfc = isIfcModel(node);
                const useOutlineLayer = isIfc && !!this.layers.modelsOutline;
                const useRaster = isIfc && !this.layers.modelsOutline;

                if (useRaster) {
                    const model = new RasterModel(node as any, this.layers.structures);
                    this.store.activeModels.push(model);
                    await model.load();
                    model.setOpacity(this.surfaceOpacityComputed, this.linesOpacityComputed);
                } else {
                    const outlineLayer = useOutlineLayer ? this.layers.modelsOutline : this.layers.models;
                    const model = new OutlineModel(node as any, outlineLayer);
                    this.store.activeModels.push(model);
                    await model.load();
                }

                this.modelsInLoading.delete(node.id);
            }

            if (counts.pipeNetworks > 0) {
                await this.layers.pipes.fetchAndDrawFile(node.id);
            }

            if (counts.mapLayers > 0) {
                try {
                    await this.backgroundMaps.onModelChecked(node.uuid, node as any);
                } catch {
                    this.store.setChecked(node.id, false);
                }
            }

            if (isMapLayerWith3DDrawableContent) {
                Events.on('load_3d_content_' + node.uuid, async (_, data: { nodeToBeLoaded: TreeNode }) => {
                    await this.drawOutlinesAndPoints(data.nodeToBeLoaded, data.nodeToBeLoaded.ajaxData.layerCounts);
                });
                Events.on('unload_3d_content_' + node.uuid, async (_, data: { nodeToBeLoaded: TreeNode }) => {
                    this.layers.stringlines.removeFeaturesBySource({ modelId: data.nodeToBeLoaded.id, modelName: data.nodeToBeLoaded.text });
                    this.layers.points.removeFeaturesBySource({ modelId: data.nodeToBeLoaded.id, modelName: data.nodeToBeLoaded.text });
                });
            }
        } catch (e) {
            console.error('Failed to load file', node.id, e);
            this.store.setChecked(node.id, false);
            this.store.setError(node.id, 'Failed to load model');
        } finally {
            this.store.setLoading(node.id, false);
            this.modelsInLoading.delete(node.id);
        }
    }

    private unloadFile(node: TreeNode) {
        if (!node) return;

        const counts = node.ajaxData?.layerCounts;
        const id = node.id;
        const name = node.text;
        const uuid = node.uuid;

        this.layers.pipes.removeFeaturesBySource({ modelId: id });
        this.layers.alignments.removeFeaturesBySource({ modelId: id });
        this.layers.points.removeFeaturesBySource({ modelId: id, modelName: name });
        this.layers.tekPoints?.removeFeaturesBySource({ modelId: id, modelName: name });
        this.layers.stringlines.removeFeaturesBySource({ modelId: id, modelName: name }, false);
        this.layers.models.removeFeaturesBySource({ modelId: id, modelName: name });
        this.layers.modelsOutline?.removeFeaturesBySource({ modelId: id, modelName: name });

        if (counts?.mapLayers > 0) {
            this.backgroundMaps.onModelUnchecked(uuid);
        }

        if (counts?.mapLayers > 0 && counts?.features > 0) {
            Events.off('load_3d_content_' + uuid);
            Events.off('unload_3d_content_' + uuid);
        }

        const model = this.store.activeModels.find(m => m.uuid === uuid);
        if (model) {
            try {
                model.dispose();
            } catch (e) {
                console.warn('Failed to dispose model', uuid, e);
            }
            this.store.activeModels.splice(
                this.store.activeModels.findIndex(m => m.uuid === uuid),
                1
            );
        }

        this.store.setLoading(node.id, false);
    }

    private async drawOutlinesAndPoints(treeNode: TreeNode, counts: LayerCounts): Promise<void> {
        const response = await this.layers.models.fetchFile(treeNode.id);

        if (response.outlines.props && response.outlines.props.name === 'tek') {
            this.layers.tekPoints?.drawAll(response.outlines.points);
        } else {
            this.layers.points.drawAll(response.outlines.points);
        }

        const pointToLineCodeMap = response.outlines.points.reduce((map: Map<string, string>, point: IOutlinePoint) => {
            const key = `${point.lon}_${point.lat}`;
            if (point.code) map.set(key, point.code);
            return map;
        }, new Map<string, string>());

        response.outlines.layers.forEach((layer: IOutlineLayer) => {
            layer.polylines.forEach((polyline: IOutlinePolyline) => {
                const lineCodes = polyline.points.map((point: IOutlinePoint) => pointToLineCodeMap.get(`${point.lon}_${point.lat}`));
                const uniqueLineCodes = Array.from(new Set(lineCodes.filter(Boolean)));
                if (uniqueLineCodes.length === 1) {
                    polyline.props = polyline.props || {};
                    polyline.props.lineCode = uniqueLineCodes[0];
                }
            });
        });

        this.layers.stringlines.drawAllFromOutlineLayers(response.outlines.layers, treeNode.ajaxData?.name ?? treeNode.text);

        if (counts.triangleMeshes === 0) {
            this.layers.models.drawAll(response.outlines.layers);
        }
    }

    /* ─── Public API (used by external code) ─── */

    public checkIds(ids: number[] = []) {
        ids.forEach(id => this.tree?.getById(id)?.setChecked(1));
        this.tree?.calculateCheckboxes();
        this.store.setCheckedIds(Array.from(new Set([...this.store.checkedIds, ...ids])));
    }

    public checkUuids(uuids: string[] = []) {
        const ids = uuids.map(uuid => this.tree?.getByUuid(uuid)?.id).filter((id): id is number => id != null);
        ids.forEach(id => this.tree?.getById(Number(id))?.setChecked(1));
        this.tree?.calculateCheckboxes();
        this.store.setCheckedIds(Array.from(new Set([...this.store.checkedIds, ...ids])));
    }

    public getChecked(): TreeNode[] {
        return this.store.checkedIds.map(id => this.tree?.getById(Number(id))).filter((n): n is TreeNode => !!n && n.isFile());
    }

    public getNodeExtent(node: TreeNode): olExtent.Extent {
        let extent = olExtent.createEmpty();
        if (!node?.ajaxData?.layerCounts) return extent;

        const counts = node.ajaxData.layerCounts;
        const id = node.id;

        if (counts.pipeNetworks > 0) {
            olExtent.extend(extent, featuresExtent(this.layers.pipes.getFeaturesBySource({ modelId: id })));
        }
        if (counts.alignments > 0) {
            const feature = this.layers.alignments.getFeatureById(id);
            if (feature) {
                olExtent.extend(extent, feature.getGeometry().getExtent());
            } else {
                for (let i = 0; i < counts.alignments; i++) {
                    const f = this.layers.alignments.getFeatureById(id + '_' + i);
                    if (f) olExtent.extend(extent, f.getGeometry().getExtent());
                }
            }
        }
        if (counts.triangleMeshes > 0 || counts.stringlines > 0) {
            olExtent.extend(extent, featuresExtent(this.layers.models.getFeaturesBySource({ modelId: id })));
            const outlineExtent = this.layers.modelsOutline?.getExtentByModelId(id);
            if (outlineExtent) olExtent.extend(extent, outlineExtent);
            const model = this.store.activeModels.find(m => m.uuid === node.uuid);
            if (model?.extent) olExtent.extend(extent, model.extent);
        }
        if (counts.breaklineLayers > 0) {
            olExtent.extend(extent, featuresExtent(this.layers.stringlines.getFeaturesBySource({ modelId: id })));
        }
        if (counts.pointsLayers > 0) {
            extent = featuresExtent(this.layers.points.getFeaturesBySource({ modelId: id }));
            olExtent.extend(extent, featuresExtent(this.layers.tekPoints?.getFeaturesBySource({ modelId: id }) ?? []));
        }
        if (counts.features > 0) {
            olExtent.extend(extent, featuresExtent(this.layers.models.getFeaturesBySource({ modelId: id })));
            const outlineExtent = this.layers.modelsOutline?.getExtentByModelId(id);
            if (outlineExtent) olExtent.extend(extent, outlineExtent);
            olExtent.extend(extent, featuresExtent(this.layers.stringlines.getFeaturesBySource({ modelId: id })));
            olExtent.extend(extent, featuresExtent(this.layers.points.getFeaturesBySource({ modelId: id })));
            olExtent.extend(extent, featuresExtent(this.layers.tekPoints?.getFeaturesBySource({ modelId: id }) ?? []));
        }
        if (counts.mapLayers) {
            const bgExtent = this.backgroundMaps.getExtentForDrawing(node.uuid);
            if (bgExtent) olExtent.extend(extent, bgExtent);
        }

        return extent;
    }

    /** Whether the model is currently checked/loaded. Source of truth is the store, not native tree state. */
    public isModelLoaded = (uuid: string): boolean => {
        const node = this.tree?.getByUuid(uuid);
        if (!node) return false;
        return this.store.checkedIds.includes(node.id);
    };

    /** Checks/unchecks a model by uuid and loads/unloads it on the map. Mirrors handleCheck/handleUncheck. */
    public setModelLoaded = (uuid: string, loaded: boolean): void => {
        const node = this.tree?.getByUuid(uuid);
        if (!node) return;

        this.store.setChecked(node.id, loaded);
        if (loaded) {
            void this.loadFile(node);
            return;
        }
        this.unloadFile(node);
    };

    public async loadModelWithPreload(headUuid: string): Promise<void> {
        if (!headUuid) return;

        const treeNode = this.tree?.getByHeadUuid(headUuid);
        if (!treeNode) return;

        this.store.setChecked(treeNode.id, true);
        await this.loadFile(treeNode);
    }

    public clearSelection = () => {
        // Deselect in the store first so the tree UI always clears, even if
        // unloading one of the files throws — unload errors must not leave
        // checkboxes stuck checked.
        const idsToUnload = [...this.store.checkedIds];
        this.store.setCheckedIds([]);
        this.store.setSelectedId(null);

        idsToUnload.forEach(id => {
            const nativeNode = this.tree?.getById(Number(id));
            if (!nativeNode) return;
            try {
                this.unloadFile(nativeNode);
            } catch (e) {
                console.error('Failed to unload model', id, e);
            }
        });
    };

    public clearModels = () => {
        const idsToUnload = this.store.checkedIds.filter(id => {
            const node = this.tree?.getById(Number(id));
            return !(node?.ajaxData?.layerCounts?.mapLayers > 0);
        });
        idsToUnload.forEach(id => {
            const nativeNode = this.tree?.getById(Number(id));
            if (nativeNode) this.unloadFile(nativeNode);
        });
        const keepIds = this.store.checkedIds.filter(id => {
            const node = this.tree?.getById(Number(id));
            return node?.ajaxData?.layerCounts?.mapLayers > 0;
        });
        this.store.setCheckedIds(keepIds);
    };

    public clearDrawings = () => {
        const idsToUnload = this.store.checkedIds.filter(id => {
            const node = this.tree?.getById(Number(id));
            return node?.ajaxData?.layerCounts?.mapLayers > 0;
        });
        idsToUnload.forEach(id => {
            const nativeNode = this.tree?.getById(Number(id));
            if (nativeNode) this.unloadFile(nativeNode);
        });
        const keepIds = this.store.checkedIds.filter(id => {
            const node = this.tree?.getById(Number(id));
            return !(node?.ajaxData?.layerCounts?.mapLayers > 0);
        });
        this.store.setCheckedIds(keepIds);
    };

    public focusItem(idOrUuid: number | string) {
        if (!this.tree) {
            this.queueFocusIdOrUuid = idOrUuid;
            return;
        }
        const node = typeof idOrUuid === 'number' ? this.tree.getById(idOrUuid) : this.tree.getByUuid(String(idOrUuid)) ?? this.tree.getByHeadUuid(String(idOrUuid));
        if (node) this.store.setSelectedId(node.isFolder() ? `folder-${node.id}` : node.id);
    }

    public openInFilesPage(id: number) {
        const node = this.tree?.getById(id);
        if (node?.parent?.id != null) {
            window.location.href = window.location.origin + kuura.getContextPath() + '/models.htm#/' + node.parent.id + '/' + id;
        }
    }

    public checkItem(id: number) {
        if (!this.tree) return;

        const treeItem: TreeNode = this.tree.getById(id);
        if (!treeItem) return;

        this.tree.calculateCheckboxes();
        this.store.setChecked(id, true);
    }

    public getCheckedFileIds(): number[] {
        return this.store.checkedIds.filter((id): id is number => Number.isFinite(id));
    }

    public getCheckedHeadUuids(): string[] {
        return this.store.checkedIds
            .map(id => this.tree?.getById(Number(id)))
            .filter((n): n is TreeNode => !!n && n.isFile())
            .map(n => n.headUuid)
            .filter(Boolean);
    }

    public getCheckedFileNames(): string[] {
        return this.store.checkedIds.map(id => this.tree?.getById(Number(id))?.text).filter((n): n is string => n != null);
    }

    public openWorkflowItem = (fileUuid: string) => {
        WorkflowsService.fetchDocumentHistory(fileUuid)
            .then(resp => {
                const lastEntry = resp[0] ?? null;
                if (!lastEntry) {
                    DialogUtils.renderComponentBasic(
                        __('workflows.tasks.submit_review'),
                        <div className='flex-column of-h'>
                            <WorkflowReviewTaskEditDialog onSuccess={this.onWorkflowSuccess} onClose={() => DialogUtils.close()} initialFileUuids={[fileUuid]} />
                        </div>
                    );
                } else {
                    WorkflowsService.fetchTask(lastEntry.workflowTaskUuid).then(task => {
                        DialogUtils.renderComponentBasic(
                            __('workflows.tasks.do_review'),
                            <div className='flex-column of-h'>
                                <WorkflowApproveDialog task={task} onSuccess={this.onWorkflowSuccess} onClose={() => DialogUtils.close()} />
                            </div>
                        );
                    });
                }
            })
            .catch(Notify.error);
    };

    onWorkflowSuccess = () => {
        Notify.success();
        DialogUtils.close();
    };

    readonly selectAll = () => {
        const allFiles = this.getAllFileNodes();
        const filesToCheck = allFiles.filter(n => {
            const counts = n.ajaxData?.layerCounts;
            return counts && countsIncludesModels(counts);
        });

        if (filesToCheck.length > 30) {
            if (!confirm(kuura.getMessage('general.warning.loading_many_items') + kuura.getMessage('general.warning.slow_crashing_action'))) {
                return;
            }
        }

        Telemetry.add(TelemetryEventType.MAP_MODELS_SELECT_ALL, TelemetrySource.MAP_2D_DESKTOP);

        const newIds = filesToCheck.map(n => n.id);
        this.store.setCheckedIds(Array.from(new Set([...this.store.checkedIds, ...newIds])));

        filesToCheck.forEach(node => this.loadFile(node));
    };

    /* ─── Opacity ─── */

    updateLayersOpacity = () => {
        const surfaceOpacity = this.surfaceOpacityComputed;
        const linesOpacity = this.linesOpacityComputed;
        this.layers.models.setLayerOpacity(surfaceOpacity);
        this.layers.modelsOutline?.setLayerOpacity(surfaceOpacity);
        this.layers.structures.setLayerOpacity(surfaceOpacity);
        this.layers.alignments.setLayerOpacity(linesOpacity);
        this.layers.stringlines.setLayerOpacity(linesOpacity);
        this.layers.points.setLayerOpacity(linesOpacity);
        this.layers.tekPoints.setLayerOpacity(linesOpacity);
        this.layers.pipes.setLayerOpacity(linesOpacity);
    };

    get surfaceOpacityComputed() {
        return Math.round((this.settings.layerOpacity * this.settings.layerOpacityModels) / 100);
    }

    get linesOpacityComputed() {
        return Math.round((this.settings.layerOpacity * this.settings.layerOpacityLines) / 100);
    }

    updateDrawingsLayerOpacity = () => {
        this.backgroundMaps.layerOpacity = this.settings.layerOpacityDrawings;
    };
    setMasterLayerOpacity = (opacity: number) => {
        this.settings.layerOpacity = opacity;
        this.updateLayersOpacity();
        this.updateDrawingsLayerOpacity();
    };
    setLayerOpacityModels = (opacity: number) => {
        this.settings.layerOpacityModels = opacity;
        this.updateLayersOpacity();
    };
    setLayerOpacityLines = (opacity: number) => {
        this.settings.layerOpacityLines = opacity;
        this.updateLayersOpacity();
    };
    setLayerOpacityDrawings = (opacity: number) => {
        this.settings.layerOpacityDrawings = opacity;
        this.updateDrawingsLayerOpacity();
    };

    /* ─── Selection feedback ─── */

    clearMenuItem: IContextMenuItem[] = [
        { label: kuura.getMessage('js_map.clear_models'), action: this.clearModels },
        { label: kuura.getMessage('js_map.clear_drawings'), action: this.clearDrawings },
    ];

    /* ─── Stateful (page state persistence) ─── */

    stateful = {
        queuedRestoreState: null as IMapPageStateModelsPanel | null,

        defaultState: {
            headUuids: [],
            fileIds: [],
            fileNames: [],
            rasterizedModelIds: [],
            iconLabelProps: [],
            lineLabelProps: [],
            showLinePoints: false,
            showDeviations: false,
            noNamesInLineLabel: false,
            layerOpacity: 100,
            layerOpacityModels: 100,
            layerOpacityLines: 100,
            layerOpacityDrawings: 100,
        },

        getCurrentState: (): IMapPageStateModelsPanel => {
            if (!this.enabled()) return null;
            return {
                headUuids: this.getCheckedHeadUuids(),
                fileIds: this.getCheckedFileIds(),
                fileNames: this.getCheckedFileNames(),
                rasterizedModelIds: Array.from(this.backgroundMaps.getRasterizedModelIds()),
                iconLabelProps: this.layers.points.labelProps,
                lineLabelProps: this.layers.stringlines.lineLabelProps,
                noNamesInLineLabel: this.layers.stringlines.noNamesInLabel,
                showLinePoints: this.layers.stringlines.showLinePoints,
                showDeviations: this.layers.points.showDeviations,
                layerOpacity: this.settings.layerOpacity,
                layerOpacityModels: this.settings.layerOpacityModels,
                layerOpacityLines: this.settings.layerOpacityLines,
                layerOpacityDrawings: this.settings.layerOpacityDrawings,
            };
        },

        restoreDefaultState: () => {
            if (!this.enabled()) return;
            this.stateful.restoreState(this.stateful.defaultState);
        },

        restoreState: (state: Partial<IMapPageStateModelsPanel>): void => {
            if (!state || !this.enabled()) return;

            if (!this.tree && state.headUuids) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state));
                return;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'iconLabelProp')) {
                this.layers.points.setLabelProps([state.iconLabelProp]);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'iconLabelProps')) {
                this.layers.points.setLabelProps(state.iconLabelProps);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'lineLabelProps')) {
                this.layers.stringlines.setLineLabelProps(state.lineLabelProps);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'layerOpacity')) {
                this.setMasterLayerOpacity(state.layerOpacity);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'layerOpacityModels')) {
                this.setLayerOpacityModels(state.layerOpacityModels);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'layerOpacityLines')) {
                this.setLayerOpacityLines(state.layerOpacityLines);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'layerOpacityDrawings')) {
                this.setLayerOpacityDrawings(state.layerOpacityDrawings);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'noNamesInLineLabel')) {
                this.layers.stringlines.setNoNamesInLabels(state.noNamesInLineLabel);
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'showLinePoints')) {
                this.layers.stringlines.showLinePoints = state.showLinePoints;
            }
            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'showDeviations')) {
                this.layers.points.setShowDeviations(state.showDeviations);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateMapsPanel>(state, 'rasterizedModelIds') && Array.isArray(state.rasterizedModelIds)) {
                this.backgroundMaps.getRasterizedModelIds().clear();
                state.rasterizedModelIds.forEach(id => this.backgroundMaps.toggleRasterizedModelId(id));
            }

            // be careful to check/load nodes by headUuids and not IDs, check comment in types
            const stateHeadUuids = Array.isArray(state.headUuids) ? state.headUuids : [];
            const stateFileIds = Array.isArray(state.fileIds) ? state.fileIds : [];

            // run even when both lists are empty, so a view saved with no checked models unloads everything
            if (Array.isArray(state.headUuids) || Array.isArray(state.fileIds)) {
                const nodesToLoad: TreeNode[] = [];
                const length = Math.max(stateHeadUuids.length, stateFileIds.length);
                for (let i = 0; i < length; i++) {
                    const headUuid = stateHeadUuids[i];
                    const fileId = stateFileIds[i];

                    let node = headUuid ? this.tree?.getByHeadUuid(headUuid) : null;
                    if (!node && fileId != null) {
                        node = this.tree?.getById(Number(fileId));
                    }
                    if (node) nodesToLoad.push(node);
                }

                const keepIds = new Set(nodesToLoad.map(n => n.id));

                // unload files that are currently loaded but not in saved state
                this.store.checkedIds.forEach(id => {
                    if (keepIds.has(Number(id))) {
                        return;
                    }
                    const node = this.tree?.getById(Number(id));
                    if (node) this.unloadFile(node);
                });

                this.store.setCheckedIds(nodesToLoad.map(n => n.id));
                // Load files that were checked in saved state
                nodesToLoad.forEach(node => this.loadFile(node));
            }

            this.stateful.queuedRestoreState = null;
        },
    };
}
